/* eslint-disable */
import React, { useContext } from 'react';

import { Grid, Skeleton } from '@mui/material';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ClassContext } from '../../../ClassContext';
import { StyledActionButton } from '../../../styles/table-styles';

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

export const ClassDetailStudy = () => {
    const { classData } = useContext(ClassContext);
    console.log('ClassDetailStudy', classData);
    const { t } = useTranslation('portal');

    const handleCreateCustomFlashcardsClicked = () => {
        console.log('handleCreateCustomFlashcardsClicked');
    };

    const summary =
        'Over the semester, sixth-grade students delved into the fascinating realm of trees, starting with recognizing various species and understanding their crucial role in ecosystems. Through interactive sessions, they explored complex topics like photosynthesis, seasonal adaptations, and the diverse habitats trees foster, alongside discussing the challenges trees endure from diseases, pests, and deforestation. The course culminated with students appreciating the historical, cultural, and economic significance of trees, while engaging in community projects that highlighted the impact and importance of local tree species in their surroundings.';

    const topicData = [
        {
            classSessionDate: new Date('2023/10/05'),
            classSessionSummary:
                'Introduction to the world of trees and their importance in ecosystems. Explore various species of trees and their distinguishing features.',
            topics: [
                'Introduction to trees',
                'Importance of trees',
                'Different species of trees',
                'Tree identification basics'
            ],
            questions: [
                'What makes trees different from other plants?',
                'How can we identify different species of trees?',
                'Why are trees important to ecosystems?'
            ],
            hardTopics: ['Different species of trees', 'Tree identification basics'],
            flashcards: [
                {
                    topic: 'Different species of trees',
                    question: 'What are some common species of trees?',
                    answer: 'Some common species of trees include Oak, Maple, Pine, Birch, and Cherry.'
                },
                {
                    topic: 'Tree identification basics',
                    question: 'What are the common ways to identify different tree species?',
                    answer: 'Trees can be identified by their leaves, bark, flowers, fruit, and overall structure.'
                },
                {
                    topic: 'Importance of trees',
                    question: 'How do trees help in reducing soil erosion?',
                    answer: 'Trees help in reducing soil erosion by holding the soil together with their roots, which also helps in preventing runoff.'
                }
            ]
        },
        {
            classSessionDate: new Date('2023/10/19'),
            classSessionSummary:
                'Diving into the process of photosynthesis and how trees contribute to the oxygen cycle. Discussion about the parts of a tree and their functions.',
            topics: ['Photosynthesis', 'Oxygen cycle', 'Parts of a tree', 'Function of each part'],
            questions: [
                'How do trees make oxygen?',
                'What are the main parts of a tree and what do they do?',
                "Why can't other plants perform photosynthesis as effectively as trees?"
            ],
            hardTopics: ['Photosynthesis', 'Function of each part'],
            flashcards: [
                {
                    topic: 'Photosynthesis',
                    question: 'What is the basic equation for photosynthesis?',
                    answer: '6 CO2 + 6 H2O + light energy = C6H12O6 + 6 O2.'
                },
                {
                    topic: 'Function of each part',
                    question: 'What is the function of tree roots?',
                    answer: 'Tree roots provide support, absorb water and nutrients, and store energy.'
                },
                {
                    topic: 'Photosynthesis',
                    question: 'What are the products of photosynthesis?',
                    answer: 'The products of photosynthesis are glucose (C6H12O6) and oxygen (O2).'
                }
            ]
        },
        {
            classSessionDate: new Date('2023/11/02'),
            classSessionSummary:
                'Exploring the various habitats that trees create and support. Discussion on how different animals and plants coexist in these habitats.',
            topics: [
                'Tree habitats',
                'Animal and plant coexistence',
                'Biodiversity in tree habitats',
                'Importance of tree habitats'
            ],
            questions: [
                'What kinds of animals live in tree habitats?',
                'How do trees provide shelter for other plants and animals?',
                'Why is biodiversity important in tree habitats?'
            ],
            hardTopics: ['Animal and plant coexistence', 'Biodiversity in tree habitats'],
            flashcards: [
                {
                    topic: 'Animal and plant coexistence',
                    question: 'How do animals and plants coexist in tree habitats?',
                    answer: 'Animals and plants coexist in tree habitats through symbiotic relationships, where they benefit from each other for food, shelter, and survival.'
                },
                {
                    topic: 'Biodiversity in tree habitats',
                    question: 'Why is biodiversity important in tree habitats?',
                    answer: 'Biodiversity in tree habitats promotes a healthy and resilient ecosystem, ensuring survival during changes and providing various resources.'
                },
                {
                    topic: 'Animal and plant coexistence',
                    question: 'Give an example of a symbiotic relationship between a tree and an animal.',
                    answer: 'A symbiotic relationship example is that of bees and flowers, where bees help in pollination, while getting nectar as food from flowers.'
                }
            ]
        },
        {
            classSessionDate: new Date('2023/11/16'),
            classSessionSummary:
                'Learning about the seasons and how they affect trees and the surrounding environment. Discussion on how trees adapt to different seasons.',
            topics: [
                'Seasonal changes',
                'Tree adaptation to seasons',
                'Effects of seasons on environment',
                'Importance of seasons in tree life cycle'
            ],
            questions: [
                'How do trees prepare for winter?',
                'What changes occur in trees during different seasons?',
                'How do seasons affect the growth of trees?'
            ],
            hardTopics: ['Tree adaptation to seasons', 'Effects of seasons on environment'],
            flashcards: [
                {
                    topic: 'Tree adaptation to seasons',
                    question: 'How do trees adapt to winter season?',
                    answer: 'Trees adapt to winter by going dormant, shedding leaves to conserve energy and water.'
                },
                {
                    topic: 'Effects of seasons on environment',
                    question: 'How do seasons affect the animals living in tree environments?',
                    answer: 'Seasons affect animal behavior, food availability, and migration patterns in tree environments.'
                },
                {
                    topic: 'Seasonal changes',
                    question: 'What is one way trees adapt to spring season?',
                    answer: 'In spring, trees break dormancy and begin to bud and grow as temperatures warm and daylight increases.'
                }
            ]
        },
        {
            classSessionDate: new Date('2023/11/30'),
            classSessionSummary:
                'Understanding the dangers trees face like diseases, pests, and deforestation. Exploring the impacts of these threats on the environment and ways to mitigate them.',
            topics: [
                'Tree diseases',
                'Pests',
                'Deforestation',
                'Environmental impact of tree loss',
                'Mitigation strategies'
            ],
            questions: [
                'What kind of diseases can affect trees?',
                'How does deforestation impact our environment?',
                'What can we do to prevent tree diseases and pests?'
            ],
            hardTopics: ['Tree diseases', 'Deforestation', 'Mitigation strategies'],
            flashcards: [
                {
                    topic: 'Tree diseases',
                    question: 'Name one common tree disease and its impact.',
                    answer: 'Dutch Elm Disease, caused by a fungus, can lead to wilting, yellowing, and loss of leaves, eventually killing the tree.'
                },
                {
                    topic: 'Deforestation',
                    question: 'What is deforestation and why is it a concern?',
                    answer: "Deforestation is the clearing or removal of trees in a forest, often to clear land for other uses, and it's a concern because it leads to loss of habitat, climate change, and soil erosion."
                },
                {
                    topic: 'Mitigation strategies',
                    question: 'Name one strategy to mitigate the impacts of deforestation.',
                    answer: 'Afforestation, which is the process of planting trees on deforested land, can help mitigate the impacts of deforestation.'
                },
                {
                    topic: 'Tree diseases',
                    question: 'How can tree diseases be prevented or controlled?',
                    answer: 'Tree diseases can be prevented or controlled through proper pruning, usage of disease-resistant varieties, and application of fungicides or other treatments.'
                }
            ]
        },
        {
            classSessionDate: new Date('2023/12/14'),
            classSessionSummary:
                'Reviewing the significance of trees in culture, history, and economy. Creating and sharing projects on the local trees and their impact on the community.',
            topics: [
                'Trees in culture and history',
                'Economic importance of trees',
                'Local tree species',
                'Community impact project'
            ],
            questions: [
                'How have trees been symbolized in different cultures?',
                'Why are trees valuable to our economy?',
                'What types of trees are native to our area?'
            ],
            hardTopics: ['Trees in culture and history', 'Economic importance of trees'],
            flashcards: [
                {
                    topic: 'Trees in culture and history',
                    question: 'How have trees been symbolized in different cultures?',
                    answer: 'Trees have been symbolized as life, knowledge, and growth in various cultures.'
                },
                {
                    topic: 'Economic importance of trees',
                    question: 'Why are trees economically important?',
                    answer: 'Trees are economically important for their timber, fruit, and other products, as well as their role in tourism and supporting other industries like agriculture.'
                },
                {
                    topic: 'Local tree species',
                    question: 'What is one local tree species in our area and its significance?',
                    answer: "This answer might vary based on the location. For example, in the Pacific Northwest, Douglas Fir is a local tree species that's significant for its timber value and its role in local ecosystems."
                }
            ]
        }
    ];

    if (classData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <div>
                <h2>Summary</h2>
                <p>{summary}</p>
            </div>
            <div>
                <br />
                {topicData.map((topic) => (
                    <div>
                        <br />
                        <h3>{Moment(topic.classSessionDate).format('dddd MMMM DD')}</h3>
                        <p>
                            {'Summary:'} {topic.classSessionSummary}
                        </p>
                        <p>{'Topics:'}</p>
                        <ul>
                            {topic.topics.map((topicString) => (
                                <li>{topicString}</li>
                            ))}
                        </ul>
                        <p>{'Topics you had difficulty with:'}</p>
                        <ul>
                            {topic.hardTopics.map((topicString) => (
                                <li>{topicString}</li>
                            ))}
                        </ul>
                        <p>{'Questions you asked:'}</p>
                        <ul>
                            {topic.questions.map((q) => (
                                <li>{q}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <StyledActionButton variant="contained" onClick={handleCreateCustomFlashcardsClicked}>
                {t('create-custom-flashcards-based-on-my-questions')}
            </StyledActionButton>
        </>
    );
};
